<template>
  <div class="container pt-5">

    <div class="level">
      <div class="level-left">
        <div class="title is-4 mr-3 ml-1 my-2" v-if="documentType">
          {{ documentType.type_name }}
        </div>
      </div>

      <div class="level-right buttons">
        <v-fade-transition>
          <button class="button mb-0"
            v-if="selected.length > 0"
            @click="launchConfirmDelete()">
            <span class="icon is-medium">
              <i class="fas fa-trash has-text-danger"></i>
            </span>
            <span>
              Delete {{ selected.length }} Selected Documents
            </span>
          </button>
        </v-fade-transition>
        <button class="button is-primary mb-0" @click="adding = true">
          Add Document
        </button>
        <v-search
          v-model="searchStr"
          @search="searchDocuments"
          placeholder="Search for documents"/>
      </div>
    </div>

    <article 
      v-if="!loading && documents.length == 0" 
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no documents.
      </div>
    </article>


    <table 
      v-if="!loading && documents.length > 0" 
      class="table is-hoverable is-fullwidth has-small-font is-responsive">
    
      <thead>
        <tr>
          <th>
            <v-checkbox
              :value="allSelected"
              @input="toggleAllSelected($event)">
            </v-checkbox>
          </th>
          <th
            class="text--nowrap" 
            v-for="(col, i) in headers"
            :key="i">
            {{ col.label }}
          </th>
        </tr>
      </thead>

      <tbody>

        <tr
          class="v-centered"
          v-for="document in documents"
          :key="document.id">

          <td class="hide">
            <v-checkbox
              @click.native.stop=""
              :value="selected.indexOf(document.id) != -1"
              @input="toggleSelected(document.id)">
            </v-checkbox>
          </td>

          <td data-title="Document Name">
            <span v-if="document.id != editingId">
              {{ document.document_name }}
            </span>
            <v-content-editable 
              v-else
              required
              v-model="document.document_name"/>
          </td>

          <td data-title="Description">
            <div>
              <span v-if="document.id != editingId">
                {{ document.document_desc }}
              </span>
              <v-content-editable 
                v-else
                required
                v-model="document.document_desc"/>
            </div>
          </td>

          <td data-title="Document">
            <a 
              v-if="document.attachment" 
              :href="document.attachment"
              target="_blank">
              link
            </a>
          </td>

          <td data-title="Created On" class="text--nowrap">
            {{ formatDate(parseDate(document.created_on)) }}
          </td>

          <td data-title="Created By" class="text--nowrap">
            {{ document.created_user.display_name }}
          </td>

          <!-- record actions -->
          <td>
            <div class="buttons buttons-right">
              <template v-if="document.id != editingId">
                <button
                  class="button is-small is-primary"
                  @click.stop="editingId = document.id">
                  <span class="icon is-medium">
                    <i class="fas fa-edit"></i>
                  </span>
                  <span>
                    Edit
                  </span>
                </button>
                <button
                  class="button is-small has-text-grey"
                  @click.stop="launchConfirmDelete(document.id)">
                  <span class="icon is-medium">
                    <i class="fas fa-trash"></i>
                  </span>
                  <span>
                    Delete
                  </span>
                </button>
              </template>
              <template v-else>
                <button
                  class="button is-small is-primary"
                  @click.stop="update(document)">
                  <span class="icon is-medium">
                    <i class="fas fa-save"></i>
                  </span>
                  <span>
                    Save Changes
                  </span>
                </button>
                <button
                  class="button is-small has-text-grey"
                  @click.stop="editingId = null">
                  <span class="icon is-medium">
                    <i class="fas fa-times"></i>
                  </span>
                  <span>
                    Exit Editing
                  </span>
                </button>
              </template>
            </div>
          </td>

        </tr>

      </tbody>
    </table>

    <add-document-modal 
      v-model="adding" 
      :document-type-id="$route.params.documentTypeId"/>

    <!-- pagination -->
    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>
    
    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb()"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VContentEditable from '@/components/base/VContentEditable'
import VSearch from '@/components/base/VSearch'
import VPagination from '@/components/base/VPagination'
import VConfirmModal from '@/components/base/VConfirmModal'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import AddDocumentModal from '@/components/documents/AddDocumentModal'

import { parseJSON, format } from 'date-fns'

import { mapState, mapMutations, mapActions } from 'vuex'

export default {
  components: {
    VCheckbox, VContentEditable, VSearch, VFadeTransition, 
    VPagination, VConfirmModal, AddDocumentModal
  },
  data() {
    return {
      parseDate: (date) => { return date ? parseJSON(date) : date },
      formatDate: (date) => format(date, "yyyy-MM-dd"),
      headers: [
          { label: 'Document Name', field: 'document_name', format: '' },
          { label: 'Description', field: 'document_desc', format: '' },
          { label: 'Document', field: 'attachment', format: '' },
          { label: 'Created On', field: 'created_on', format: 'date' },
          { label: 'Created By', field: 'created_by', format: '' },
          { label: '' }
      ],
      searchStr: null,
      adding: false,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this document?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      editingId: null
    }
  },
  computed: {
    ...mapState("documents", [
      "documents", "selected", "allSelected", "loading", "pagination", 
      "filters", "documentTypes"
    ]),
    documentType() {
      if (this.$route.params.documentTypeId) {
        return this.documentTypes.filter(d => d.id == this.$route.params.documentTypeId)[0];
      } else {
        return null
      }
    },
    documentTypeId() {
      return this.$route.params.documentTypeId;
    }
  },
  watch: {
    'pagination.page'(newPage) {
      let parameters = this.filters;
      parameters.page = newPage;
      this.getDocuments(parameters);
    },
    filters: {
      handler(filters) {
        this.getDocuments(filters);
      },
      deep: true
    },
    documentTypeId (id) {
      this.setFilter({
        field: "document_type_id", 
        value: id
      });
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("documents", [
      "getDocuments", "addDocument", "updateDocument", "addAttachment", 
      "deleteDocument", "toggleAllSelected"
    ]),
    ...mapMutations("documents", [
      "changePage", "toggleSelected", "setFilter"
    ]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.$route.params.documentTypeId) {
        this.setFilter({
          field: "document_type_id", 
          value: this.$route.params.documentTypeId
        });
      } else {
        this.getDocuments();
      }
    },
    update(document) {
      this.updateDocument({
        documentId: document.id,
        data: {
          document_name: document.document_name,
          document_desc: document.document_desc
        }
      }).then(() => {
        this.setNotification({
          status: true,
          type: 'success',
          message: 'Changes successfully saved'
        });
        this.editingId = null;
      })
    },
    searchDocuments(v) {
      let parameters = {...this.filters};
      parameters.q = v;
      this.getDocuments(parameters);
    },
    launchConfirmDelete(id) {
      if (!id) {
        this.deleteConfirmedCb = () => {
          this.selected.forEach(id => {
            this.deleteDocument(id);
            this.confirmDelete.state = false;
          })
        }
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} documents?`;
        this.confirmDelete.state = true;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteDocument(id);
          this.confirmDelete.state = false;
        }
        this.confirmDelete.message = 'Are you sure you want to delete this document?';
        this.confirmDelete.state = true;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>
